import { CartAddEvent } from '@theme/events';

const drawerSelector = 'cart-drawer-component';
const cartItemsModuleUrl = new URL('component-cart-items.js', import.meta.url).href;
const cartQuantityModuleUrl = new URL('component-cart-quantity-selector.js', import.meta.url).href;

function drawerSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-drawer][data-section-id]')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean)
    .filter((sectionId, index, sectionIds) => sectionIds.indexOf(sectionId) === index);
}

async function readCart() {
  const url = new URL('/cart.js', window.location.origin);
  url.searchParams.set('_', String(Date.now()));
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Unable to refresh the cart.');
  return response.json();
}

function waitForDrawerItems(expectedItemCount, timeout = 2400) {
  const ready = () => {
    const drawer = document.querySelector(drawerSelector);
    const countText = drawer?.querySelector('.elx-cart-drawer__item-count')?.textContent || '';
    const renderedItemCount = Number(countText.match(/\d+/)?.[0]);
    return renderedItemCount === expectedItemCount && Boolean(drawer?.querySelector('.cart-items__table-row[data-key]'));
  };

  if (ready()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      if (!ready()) return;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(true);
    });
    const timer = window.setTimeout(() => {
      observer.disconnect();
      resolve(ready());
    }, timeout);
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  });
}

function openDrawer() {
  const drawer = document.querySelector(drawerSelector);
  if (typeof drawer?.open === 'function') drawer.open();
  else document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
}

async function waitForCartComponents() {
  const imports = [];
  if (!customElements.get('cart-items-component')) imports.push(import(cartItemsModuleUrl));
  if (!customElements.get('cart-quantity-selector-component')) imports.push(import(cartQuantityModuleUrl));
  if (imports.length) await Promise.all(imports);

  await Promise.all([
    customElements.whenDefined('cart-items-component'),
    customElements.whenDefined('cart-drawer-component'),
  ]);
}

/**
 * Adds cart items through the same event contract Horizon uses for native product forms.
 * The drawer's cart-items component owns section morphing; callers never replace its DOM.
 */
async function addItemsToCart({ items, sourceId, source, productId = null }) {
  if (!Array.isArray(items) || !items.length) return null;

  await waitForCartComponents();

  const sectionIds = drawerSectionIds();
  const payload = { items };
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.description || body.message || 'Unable to add to cart.');
  }

  const addResult = await response.json().catch(() => ({}));
  const cart = await readCart();
  document.dispatchEvent(new CartAddEvent(cart, sourceId || 'elx-cart-actions', {
    source: source || 'elx-cart-actions',
    itemCount: Number(cart.item_count) || 0,
    productId,
    sections: addResult.sections || {},
  }));

  if (await waitForDrawerItems(Number(cart.item_count) || 0)) openDrawer();
  return cart;
}

window.ElxCartActions = Object.freeze({ addItemsToCart });
